/**
 * Sprint T-16.1 · Automation Center · RunningTab.
 *
 * Ejecuciones status=running con auto-refresh 5s (§17.4).
 * Muestra heartbeat + duración en curso.
 */
import React, { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { WidgetCard, EmptyState, LoadingState, CitationBadge } from "../advisor";
import { useExecutions } from "../../hooks/useAutomation";
import { StatusBadge, TriggerBadge, RetryBadge, DurationBadge } from "./shared/Badges";
import { RelativeTime, AbsoluteTime } from "./shared/Formatters";
import { ExecutionDetailPanel } from "./shared/DetailPanels";


function RunningCard({ exec, onSelect }) {
  const testId = `running-card-${exec.id}`;
  const stale = exec.heartbeat_at
    ? (Date.now() - new Date(exec.heartbeat_at).getTime()) > 60000
    : false;
  const startedAt = exec.started_at ? new Date(exec.started_at).getTime() : null;
  const elapsedMs = startedAt ? Date.now() - startedAt : null;
  return (
    <article
      data-testid={testId}
      className={`rounded-lg border p-3 ${
        stale ? "border-amber-500/40 bg-amber-500/5" : "border-blue-500/30 bg-blue-500/5"
      }`}
    >
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <StatusBadge status={exec.status} />
            <TriggerBadge trigger={exec.trigger} />
            <RetryBadge attempt={exec.attempt} max={exec.max_attempts} />
            {stale && (
              <span
                data-testid={`${testId}-stale`}
                className="inline-flex items-center gap-1 rounded border border-amber-500/40 bg-amber-500/10 px-1 py-0.5 text-[9px] text-amber-500"
              >
                <AlertTriangle className="h-2.5 w-2.5" /> heartbeat stale
              </span>
            )}
          </div>
          <div className="mt-1 font-mono text-xs font-semibold">{exec.job_name}</div>
          <div className="text-[10px] text-[var(--ag-text-dim)]">
            run_id <code className="font-mono">{exec.run_id?.slice(0, 8)}</code>
            {" · leased_by "}
            <code className="font-mono">{exec.leased_by || "?"}</code>
          </div>
        </div>
        <button
          type="button"
          data-testid={`${testId}-detail`}
          onClick={() => onSelect?.(exec.id)}
          className="rounded border border-[var(--ag-border)] px-2 py-1 text-[10px] hover:bg-[var(--ag-surface-2)]"
        >
          Detalle
        </button>
      </header>
      <dl className="mt-2 grid grid-cols-2 gap-2 text-[10px]">
        <div>
          <dt className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Iniciado</dt>
          <dd><RelativeTime iso={exec.started_at} /></dd>
        </div>
        <div>
          <dt className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Último heartbeat</dt>
          <dd><RelativeTime iso={exec.heartbeat_at} /></dd>
        </div>
        <div>
          <dt className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">En curso</dt>
          <dd><DurationBadge ms={elapsedMs} /></dd>
        </div>
        <div>
          <dt className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Lease hasta</dt>
          <dd><AbsoluteTime iso={exec.leased_until} /></dd>
        </div>
      </dl>
    </article>
  );
}


export default function RunningTab() {
  const [selected, setSelected] = useState(null);
  const q = useExecutions({ status: "running", limit: 100 }, { poll: 5000 });
  const items = q.data?.items || [];

  return (
    <section className="space-y-4" data-testid="automation-running-tab">
      <WidgetCard
        title={`Ejecuciones activas · ${items.length}`}
        description="Vista en tiempo real · auto-refresh cada 5s · warning si heartbeat > 60s."
        citations={["/api/automation/executions?status=running"]}
        testId="widget-running-list"
      >
        {q.isLoading && <LoadingState />}
        {q.error && <EmptyState message="No se pudo cargar." />}
        {q.data && items.length === 0 && (
          <EmptyState
            title="🟢 Sin ejecuciones activas"
            message="El worker está idle · próxima tarea llegará según schedule."
            testId="running-empty"
          />
        )}
        {items.length > 0 && (
          <div
            className="grid grid-cols-1 gap-2 md:grid-cols-2"
            data-testid="running-grid"
          >
            {items.map((e) => (
              <RunningCard key={e.id} exec={e} onSelect={setSelected} />
            ))}
          </div>
        )}
      </WidgetCard>

      {selected && <ExecutionDetailPanel executionId={selected} onClose={() => setSelected(null)} />}

      <footer className="border-t border-[var(--ag-border)] pt-3">
        <CitationBadge sources={["auto-refresh 5s", "recover_zombies backend cada tick"]} />
      </footer>
    </section>
  );
}
